import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type moment from 'moment-timezone'
import { useCallback, useEffect, useState } from 'react'
import { apiFetch } from 'utils/apiFetch'
import { getIsoWithoutTheZ } from 'utils/getIsoWithoutTheZ'
import {
  ALMOND_BLOOM_KEY,
  ALMOND_HULL_SPLIT_KEY,
  degreeDaysKeyForInsect,
  phenologyKeyForInsect,
  trapCatchKeyForInsect,
} from './pestValueTypeKeys'

export type TDashboardValuesResult = {
  data: routes.Values.Response | null
  loading: boolean
  error: Error | null
  lastFetchedAt: Date | null
  refresh: () => void
}

export const useDashboardValues = ({
  propertyId,
  blockIds,
  stationLngLats,
  insectId,
  dateFrom,
  dateTo,
  includeTrapCatches = false,
  includeDegreeDays = false,
  includePhenology = false,
  includeBloom = false,
  includeHullSplit = false,
}: {
  propertyId: number | null
  blockIds: number[]
  // Weather-station lngLats (WKT) for the degree day points request — one DD line per station.
  stationLngLats: string[]
  insectId: number
  dateFrom: moment.Moment
  dateTo: moment.Moment
  includeTrapCatches?: boolean
  includeDegreeDays?: boolean
  includePhenology?: boolean
  includeBloom?: boolean
  includeHullSplit?: boolean
}): TDashboardValuesResult => {
  const [data, setData] = useState<routes.Values.Response | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null)
  const [refreshNonce, setRefreshNonce] = useState(0)

  const refresh = useCallback(() => {
    setRefreshNonce((n) => n + 1)
  }, [])

  // Using moment unix timestamps in deps so dates with the same value but new Moment
  // instances (from per-render filter recomputes) don't trigger redundant refetches.
  const dateFromMs = +dateFrom
  const dateToMs = +dateTo
  // Stable dep based on contents, so callers can pass a fresh array each render
  // without retriggering the fetch when contents haven't changed.
  const blockIdsKey = blockIds.join('|')
  const stationLngLatsKey = stationLngLats.join('|')

  useEffect(() => {
    // Every value type is permission-gated by the caller. Build each scope's request from only
    // the permitted/enabled types, and skip the fetch entirely if nothing is requestable.
    const propertyValuesRequested: Record<string, { preferredAggregationInterval: string }> = {
      ...(includeTrapCatches
        ? { [trapCatchKeyForInsect(insectId)]: { preferredAggregationInterval: 'DAILY' } }
        : {}),
      ...(includePhenology
        ? { [phenologyKeyForInsect(insectId)]: { preferredAggregationInterval: 'UNAGGREGATED' } }
        : {}),
      // Hull split is property-scoped here: at block scope the API returns every property cultivar
      // for every block, which inflates the chart. One line per cultivar at the property is correct.
      ...(includeHullSplit
        ? { [ALMOND_HULL_SPLIT_KEY]: { preferredAggregationInterval: 'UNAGGREGATED' } }
        : {}),
    }

    // Bloom is block-scoped (per-block 1% bloom date), grouped to one line per date when rendered.
    const blockValuesRequested: Record<string, { preferredAggregationInterval: string }> = {
      ...(includeBloom ? { [ALMOND_BLOOM_KEY]: { preferredAggregationInterval: 'DAILY' } } : {}),
    }

    // Degree days are requested at the points scope — one cumulative-since-biofix series per weather
    // station (anchored to the default biofix). Blocks are no longer involved in the DD series.
    const pointValuesRequested: Record<string, { preferredAggregationInterval: string }> = {
      ...(includeDegreeDays
        ? { [degreeDaysKeyForInsect(insectId)]: { preferredAggregationInterval: 'DAILY' } }
        : {}),
    }

    const hasPropertyValues = Object.keys(propertyValuesRequested).length > 0
    const hasBlockValues = blockIds.length > 0 && Object.keys(blockValuesRequested).length > 0
    const hasPointValues = stationLngLats.length > 0 && Object.keys(pointValuesRequested).length > 0

    if (propertyId === null || (!hasPropertyValues && !hasBlockValues && !hasPointValues)) {
      setData(null)

      setLoading(false)

      setError(null)

      return
    }

    let cancelled = false

    setLoading(true)

    setError(null)

    const body = {
      dateFrom: getIsoWithoutTheZ(dateFrom),
      dateTo: getIsoWithoutTheZ(dateTo),
      // Tag the call's source (like Data Export / Property Playback do) so dashboard-driven /values
      // load is attributable in request logging.
      featureReferer: 'DASHBOARD',
      ...(hasPropertyValues
        ? { properties: { propertyIds: [propertyId], valuesRequested: propertyValuesRequested } }
        : {}),
      ...(hasBlockValues ? { blocks: { blockIds, valuesRequested: blockValuesRequested } } : {}),
      ...(hasPointValues
        ? { points: { lngLats: stationLngLats, valuesRequested: pointValuesRequested } }
        : {}),
    } as routes.Values.Request

    apiFetch({ url: '/values', body })
      .then((res) => {
        if (cancelled) return

        setData(res)

        setLastFetchedAt(new Date())
      })
      .catch((err) => {
        if (cancelled) return

        setError(err instanceof Error ? err : new Error(String(err)))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    propertyId,
    insectId,
    dateFromMs,
    dateToMs,
    refreshNonce,
    blockIdsKey,
    stationLngLatsKey,
    includeTrapCatches,
    includeDegreeDays,
    includePhenology,
    includeBloom,
    includeHullSplit,
  ])

  return { data, loading, error, lastFetchedAt, refresh }
}
